import {ChangeEvent, KeyboardEvent, ReactElement, RefObject} from 'react'
import classNames from 'classnames'
import {useTranslation} from 'react-i18next'

import {CloseIcon} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineAssistantSendIcon} from './DopamineAssistantSendIcon'
import {
  DopamineAssistantTurn,
  DopaminePromptSubmission,
} from './DopamineAssistant.types'
import './DopamineAssistantComposer.css'

const MAX_PROMPT_LENGTH = 1000

type DopamineAssistantComposerProps = {
  composerRef: RefObject<HTMLTextAreaElement>
  disabled?: boolean
  draft: string
  isRunning: boolean
  onDraftChange(draft: string): void
  onRemoveQueued(turnId: string): void
  onSubmit(submission: DopaminePromptSubmission): void
  queuedTurn: DopamineAssistantTurn | null
}

export const DopamineAssistantComposer = ({
  composerRef,
  disabled = false,
  draft,
  isRunning,
  onDraftChange,
  onRemoveQueued,
  onSubmit,
  queuedTurn,
}: DopamineAssistantComposerProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const trimmedDraft = draft.trim()
  const canSubmit = Boolean(trimmedDraft) && !disabled
  const sendLabel = isRunning ? t('DOPAMINE_QUEUE_MESSAGE') : t('SEND_MESSAGE')

  const submitDraft = (): void => {
    if (!canSubmit) return

    onSubmit({displayText: trimmedDraft, query: trimmedDraft})
    onDraftChange('')
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    onDraftChange(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (
      event.key !== KEY_CODES.ENTER ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    ) {
      return
    }

    event.preventDefault()
    submitDraft()
  }

  return (
    <div className="group shrink-0 px-4 pb-4 pt-2">
      {queuedTurn ? (
        <output
          className="dopamine-composer-queued"
          data-cy="dopamine-assistant-queued-message"
        >
          <span className="dopamine-composer-queued-label">
            {t('DOPAMINE_QUEUED_LABEL')}
          </span>
          <span className="dopamine-composer-queued-text">
            {queuedTurn.submission.displayText}
          </span>
          <button
            aria-label={t('DOPAMINE_REMOVE_QUEUED_MESSAGE')}
            className="dopamine-composer-queued-remove"
            data-cy="dopamine-assistant-remove-queued"
            type="button"
            onClick={() => onRemoveQueued(queuedTurn.id)}
          >
            <CloseIcon height={12} strokeColor="currentColor" width={12} />
          </button>
        </output>
      ) : null}
      <div className="dopamine-composer-field relative rounded-ds-xl border-0.5 border-white/10 px-4 py-3.5">
        <textarea
          aria-describedby="dopamine-assistant-composer-help"
          aria-label={t('ASK_DOPAMINE')}
          className="dopamine-assistant-composer-input abc-light placeholder-abc-light -mr-4 block max-h-40 min-h-[2.875rem] w-[calc(100%+1rem)] min-w-0 resize-none overflow-y-auto border-0 bg-transparent pr-18 text-sm leading-5.5 text-white caret-dopamine-accent outline-none placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60"
          data-cy="dopamine-assistant-composer"
          disabled={disabled}
          maxLength={MAX_PROMPT_LENGTH}
          placeholder={t('ASK_DOPAMINE')}
          ref={composerRef}
          rows={2}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <button
          aria-disabled={!canSubmit}
          aria-label={sendLabel}
          className={classNames(
            'dopamine-send-button absolute bottom-3.5 right-8 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0.5 border-white/15 bg-transparent text-dopamine-muted',
            canSubmit && 'dopamine-send-button-active',
            (!canSubmit || disabled) && 'cursor-not-allowed opacity-50',
          )}
          data-cy="dopamine-assistant-send"
          disabled={disabled || !canSubmit}
          title={sendLabel}
          type="button"
          onClick={submitDraft}
        >
          <DopamineAssistantSendIcon />
        </button>
      </div>
      <div
        className="flex flex-wrap justify-between gap-1 px-1 pt-1.5 text-10px text-gray-500 opacity-0 transition-opacity duration-base group-focus-within:opacity-100"
        id="dopamine-assistant-composer-help"
      >
        <span>{t('DOPAMINE_COMPOSER_HELP')}</span>
        {draft.length >= 900 ? (
          <span>
            {draft.length}/{MAX_PROMPT_LENGTH}
          </span>
        ) : null}
      </div>
    </div>
  )
}
